/**
 * Parse preferred width value and convert to pixel value when possible
 * @param {string} preferredWidth - The preferred width value (e.g., "1200px", "80vw", "50%")
 * @param {number} breakpointWidth - The breakpoint width to use for vw calculations
 * @param {number} fallback - Fallback pixel value if conversion isn't possible
 * @returns {number} Pixel value or fallback
 */
function parsePreferredWidth(preferredWidth, breakpointWidth, fallback) {
  if (!preferredWidth || typeof preferredWidth !== 'string') {
    return fallback;
  }

  const match = preferredWidth.match(/^([\d.]+)(.*)$/);
  if (!match) {
    return fallback;
  }

  const value = parseFloat(match[1]);
  const unit = match[2].trim();

  if (isNaN(value)) {
    return fallback;
  }

  let result;
  switch (unit) {
    case 'px':
      result = value;
      break;
    case 'vw':
      // Convert vw to pixels based on breakpoint width
      result = Math.round((value / 100) * breakpointWidth);
      break;
    case '%':
      // For percentage, we can't know the parent width, so use a reasonable approximation
      // Assume 100% of breakpoint width as a reasonable default
      result = Math.round((value / 100) * breakpointWidth);
      break;
    case 'em':
    case 'rem':
      // For em/rem, we can't know the font size context, so fall back
      // Could use 16px as default rem size, but it's not accurate
      result = fallback;
      break;
    default:
      result = fallback;
  }
  return result;
}

export default function imageValues(attributes) {
	const {
    desktopRatio,
    imageFocalPoint,
    imageMaxWidth,
    imagePreferredWidth,
    mobileRatio,
    tabletRatio,
    useSourceProportion,
    imageUrl,
    imageHeight,
    imageWidth,
  } = attributes;

  let imageSizes = {};
  let imageFocalPoints = {};
  let effectiveWidth = imageMaxWidth;
  let effectiveDesktopRatio = desktopRatio;
  let effectiveTabletRatio = tabletRatio || desktopRatio;
  let effectiveMobileRatio = mobileRatio || tabletRatio || desktopRatio;

  if (useSourceProportion && imageUrl && imageWidth && imageHeight) {
    effectiveWidth = imageWidth;
    const sourceProportion = imageHeight / imageWidth * 100;
    effectiveDesktopRatio = effectiveTabletRatio = effectiveMobileRatio = sourceProportion;
  }

  // Use preferred width for desktop (1920 breakpoint) if set
  // Support both numeric ('1920') and named ('desktop') keys for backward compatibility
  const desktopPreferredWidth1920 = imagePreferredWidth?.['1920'];
  const desktopPreferredWidthNamed = imagePreferredWidth?.['desktop'];
  // Use the first non-empty value (empty strings are falsy, so || works here)
  const desktopPreferredWidth = (desktopPreferredWidth1920 && desktopPreferredWidth1920.trim()) || (desktopPreferredWidthNamed && desktopPreferredWidthNamed.trim()) || null;
  if (desktopPreferredWidth) {
    effectiveWidth = parsePreferredWidth(desktopPreferredWidth, 1920, effectiveWidth);
  }

  const defaultImageDims = [
    effectiveWidth,
    Math.round(effectiveWidth * effectiveDesktopRatio / 100)
  ];

  let defaultKey;
  switch (true) {
    case effectiveWidth > 1024:
      defaultKey = 'min-width:1025px';
      break;
    case effectiveWidth > 576:
      defaultKey = 'min-width:577px';
      break;
    default:
      defaultKey = 'default';
  }

  imageSizes[defaultKey] = defaultImageDims;
  imageFocalPoints[defaultKey] = imageFocalPoint;

  // Calculate tablet size (1024 breakpoint)
  // Support both numeric ('1024') and named ('tablet') keys for backward compatibility
  let tabletWidth = 1024;
  const tabletPreferredWidth1924 = imagePreferredWidth?.['1024'];
  const tabletPreferredWidthNamed = imagePreferredWidth?.['tablet'];
  const tabletPreferredWidth = (tabletPreferredWidth1924 && tabletPreferredWidth1924.trim()) || (tabletPreferredWidthNamed && tabletPreferredWidthNamed.trim()) || null;
  if (tabletPreferredWidth) {
    tabletWidth = parsePreferredWidth(tabletPreferredWidth, 1024, tabletWidth);
  } else if (effectiveWidth <= 1024) {
    tabletWidth = effectiveWidth;
  }

  if (effectiveWidth > 1024 || tabletWidth !== 1024) {
    imageSizes['max-width:1024px'] = [
      tabletWidth,
      Math.round(tabletWidth * effectiveTabletRatio / 100)
    ];
    imageFocalPoints['max-width:1024px'] = imageFocalPoint;
  }

  // Calculate mobile size (576 breakpoint)
  // Support both numeric ('576') and named ('mobile') keys for backward compatibility
  let mobileWidth = 576;
  const mobilePreferredWidth576 = imagePreferredWidth?.['576'];
  const mobilePreferredWidthNamed = imagePreferredWidth?.['mobile'];
  const mobilePreferredWidth = (mobilePreferredWidth576 && mobilePreferredWidth576.trim()) || (mobilePreferredWidthNamed && mobilePreferredWidthNamed.trim()) || null;
  if (mobilePreferredWidth) {
    mobileWidth = parsePreferredWidth(mobilePreferredWidth, 576, mobileWidth);
  } else if (effectiveWidth <= 576) {
    mobileWidth = effectiveWidth;
  } else if (tabletWidth <= 576) {
    mobileWidth = tabletWidth;
  }

  if (effectiveWidth > 576 || mobileWidth !== 576) {
    imageSizes['max-width:576px'] = [
      mobileWidth,
      Math.round(mobileWidth * effectiveMobileRatio / 100)
    ];
    imageFocalPoints['max-width:576px'] = imageFocalPoint;
  }

  return [imageSizes, imageFocalPoints];
}